// Scheduler — pure task transformers and gates (ADR-0011 PR-6b).
//
// No I/O, no scheduler-sibling deps: refresh-row → SchedulerTaskInput, the
// MS-Teams notify-target derivation, and the kanban-route feature gate.

import type { SchedulerTaskInput, SchedulerTaskState } from '../../scheduler-engine.js';

// ENG-6124: single source of truth for refresh-row → SchedulerTaskInput. Both
// sync sites call this; it exists as an exported pure function so the
// delivery_policy pass-through (the field whose omission made conditional/never
// enforcement dead on the fleet) is regression-tested directly rather than
// hidden inside two large async handlers.
export function buildSchedulerTaskInput(t: Record<string, unknown>): SchedulerTaskInput {
  return {
    id: t.id as string,
    template_id: t.template_id as string,
    name: t.name as string,
    schedule_kind: t.schedule_kind as 'cron' | 'every' | 'at',
    schedule_expr: (t.schedule_expr as string) ?? null,
    schedule_every: (t.schedule_every as string) ?? null,
    schedule_at: (t.schedule_at as string) ?? null,
    timezone: (t.timezone as string) ?? 'UTC',
    prompt: (t.prompt as string) ?? '',
    session_target: (t.session_target as string) ?? 'isolated',
    delivery_mode: (t.delivery_mode as string) ?? 'none',
    delivery_policy: (t.delivery_policy as string) ?? 'always',
    delivery_channel: (t.delivery_channel as string) ?? null,
    delivery_to: (t.delivery_to as string) ?? null,
    enabled: (t.enabled as boolean) ?? true,
    triggered_at: (t.triggered_at as string) ?? null,
  };
}


/**
 * ENG-6050: derive the materialised card's completion-notify target from a
 * scheduled task's delivery config. When the task is configured to deliver to
 * Teams (deliveryChannel='msteams' + deliveryTo=<conversation_id>), the card
 * carries notify_channel='msteams' + notify_to so the API posts the 👍/👎
 * rating card to that Teams conversation when the card closes (ENG-6050 API).
 * Returns {} for Slack/Telegram/other delivery (handled by the existing
 * deliverScheduledTaskOutput path), so this is additive and non-disruptive.
 */
export function deriveScheduledTaskNotify(
  task: Pick<SchedulerTaskState, 'deliveryChannel' | 'deliveryTo'>,
): { notify_channel?: string; notify_to?: string } {
  if (task.deliveryChannel !== 'msteams') return {};
  // delivery_to is the JSONB DeliveryTarget the webapp writes
  // ({ kind:'channel', provider:'msteams', conversation_id }); accept a bare
  // conversation-id string too (API/CLI callers).
  const to = task.deliveryTo;
  let conversationId: string | null = null;
  if (typeof to === 'string' && to.trim().length > 0) {
    conversationId = to.trim();
  } else if (to && typeof to === 'object') {
    const cid = (to as { conversation_id?: unknown }).conversation_id;
    if (typeof cid === 'string' && cid.trim().length > 0) conversationId = cid.trim();
  }
  if (!conversationId) return {};
  return { notify_channel: 'msteams', notify_to: conversationId };
}


// ---------------------------------------------------------------------------
// ENG-5599 (Slice 1 of ENG-5576): route scheduled tasks through a todo card +
// in-session nudge instead of a `claude -p` oneshot.
//
// Anthropic's billing change on `claude -p` makes a per-task oneshot
// cost-prohibitive for persistent-session agents that already have a live
// tmux session. Instead we materialise the firing as a TODO kanban card and
// nudge the live session to work it in-session — the board becomes the work
// queue (generalising the ENG-5404/5435 kanban-work cutover to all scheduled
// tasks).
//
// Cost attribution (the ENG-5557 seam): the API stamps the per-card run_id
// onto the materialised card (agent_kanban_items.run_id), and the nudge
// carries that run's marker (formatRunMarker). The per-turn transcript parser
// (ENG-5566) bounds the card's in-session turns by that marker, so
// scheduled_task_cost_windows keeps attributing cost per task exactly as it
// did for the oneshot — one run = one card = one firing — with no claude -p
// process. A board-level inject run (ENG-5565) would NOT satisfy this: the
// todo card would carry no run_id and a shared nudge run would over-attribute
// to every due task. Hence the per-card run grain here.
// ---------------------------------------------------------------------------
